import * as React from "react";

import { cn } from "~/lib/utils";

// AIDEV-NOTE: FloatingHelperCard (design name: MorphCard) — floating
// assistant/coach-mark shell. Animates its own width + height between states
// (300ms). Position it fixed bottom-right in the host. From the onboarding
// checklist design — see docs/specs/practitioner-onboarding-checklist.md.

export interface FloatingHelperCardProps
    extends React.HTMLAttributes<HTMLDivElement> {
    /** Card width in px — animate state changes by changing this. */
    width: number;
}

const FloatingHelperCard = React.forwardRef<
    HTMLDivElement,
    FloatingHelperCardProps
>(({ width, className, style, children, ...props }, ref) => {
    const innerRef = React.useRef<HTMLDivElement>(null);
    const [height, setHeight] = React.useState<number | null>(null);

    // AIDEV-NOTE: EVO-1578 collapse→expand left dead space: the inner div used
    // to follow the card's *animated* width, so the one-shot height measurement
    // was captured mid-transition (narrower = taller) and never corrected. We
    // now (a) pin the inner div to the target width below so it reflows to its
    // final layout immediately, and (b) track height with a ResizeObserver so
    // any later content reflow re-measures.
    React.useLayoutEffect(() => {
        const node = innerRef.current;
        if (!node) return;

        const measure = () => setHeight(node.offsetHeight);
        measure();

        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={cn(
                "overflow-hidden rounded-[16px] border border-brand-forest-200 bg-white shadow-[0_16px_40px_-12px_rgba(27,67,50,.28),0_2px_8px_-2px_rgba(27,67,50,.12)] transition-[height,width] duration-300 ease-[cubic-bezier(.22,1,.36,1)]",
                className,
            )}
            style={{ width, height: height ?? "auto", ...style }}
            {...props}
        >
            <div ref={innerRef} style={{ width }}>
                {children}
            </div>
        </div>
    );
});
FloatingHelperCard.displayName = "FloatingHelperCard";

export { FloatingHelperCard };
